// plan-binder.js (drop-in, no deps)
(() => {
  // -----------------------------
  // Utility: safe path resolution
  // -----------------------------
  const has = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

  function getByPath(root, rawPath) {
    if (!rawPath) return undefined;
    const path = rawPath.replace(/^bind:|^repeat:/, "");
    const tokens = [];
    path.split(".").forEach((part) => {
      const re = /([^\[\]]+)|(\[\d+\])/g;
      let m;
      while ((m = re.exec(part))) tokens.push(m[0]);
    });
    let cur = root;
    for (let tok of tokens) {
      if (cur == null) return undefined;
      if (tok.startsWith("[")) {
        const idx = parseInt(tok.slice(1, -1), 10);
        cur = Array.isArray(cur) ? cur[idx] : undefined;
      } else {
        const variants = [
          tok,
          tok.endsWith("?") ? tok : tok + "?",
          tok.replace(/\?$/, ""),
        ];
        let next;
        for (const k of variants) {
          if (cur && typeof cur === "object" && has(cur, k)) {
            next = cur[k];
            break;
          }
        }
        cur = next;
      }
    }
    return cur;
  }

  function formatValue(val, { decimals, prefix, suffix } = {}) {
    if (val == null) return "";
    let out = val;
    if (
      typeof out === "number" &&
      decimals != null &&
      !Number.isNaN(Number(decimals))
    ) {
      out = Number(out).toFixed(Number(decimals));
    }
    if (Array.isArray(out) || (typeof out === "object" && out !== null)) {
      try {
        out = JSON.stringify(out);
      } catch {}
    }
    return `${prefix || ""}${out}${suffix || ""}`;
  }

  function setValue(el, value, opts = {}) {
    const rendered = formatValue(value, opts);
    if (opts.attr) {
      el.setAttribute(opts.attr, rendered);
    } else if ("value" in el) {
      el.value = rendered;
    } else {
      el.textContent = rendered;
    }
  }

  // -----------------------------
  // Binder core (classMap, repeats)
  // -----------------------------
  function bindWithMap(root, data, classMap = {}) {
    for (const [cls, spec] of Object.entries(classMap)) {
      let els = root.getElementsByClassName(cls);
      if (!els.length) {
        const byId = root.getElementById(cls);
        if (byId) {
          els = [byId];
        } else {
          continue;
        }
      }

      const opts = typeof spec === "string" ? { path: spec } : spec;
      for (const el of els) {
        const val = getByPath(data, opts.path);
        setValue(el, val, {
          attr: opts.attr,
          decimals: opts.decimals ?? readNum(el, "data-decimals"),
          // FIX: wrap ?? part
          prefix: (opts.prefix ?? el.getAttribute("data-prefix")) || "",
          suffix: (opts.suffix ?? el.getAttribute("data-suffix")) || "",
        });
      }
    }
  }

  function readNum(el, attr) {
    const v = el.getAttribute(attr);
    return v == null ? undefined : Number(v);
  }

  function processRepeaters(root, data, repeats = {}) {
    for (const [containerCls, conf] of Object.entries(repeats)) {
      const containers = root.getElementsByClassName(containerCls);
      if (!containers.length) continue;
      for (const container of containers) {
        const list = getByPath(data, conf.path);
        if (!Array.isArray(list)) continue;

        const tpl = conf.template
          ? container.querySelector(conf.template)
          : container.querySelector("template");
        if (!tpl) continue;

        // clear clones
        [...container.children].forEach((ch) => {
          if (ch !== tpl) ch.remove();
        });

        list.forEach((item) => {
          const frag = tpl.content.cloneNode(true);
          // per-item map (your classes inside the template)
          if (conf.itemMap) {
            for (const [cls, spec] of Object.entries(conf.itemMap)) {
              const nodes = frag.querySelectorAll(`.${cls}`);
              const s = typeof spec === "string" ? { path: spec } : spec;
              nodes.forEach((el) => {
                const rawPath = s.path || "";
                const rel = rawPath.startsWith("@.")
                  ? rawPath.slice(2)
                  : rawPath;
                const val = getByPath(item, rel);
                setValue(el, val, {
                  attr: s.attr,
                  decimals: s.decimals ?? readNum(el, "data-decimals"),
                  // FIX: wrap ?? part
                  prefix: (s.prefix ?? el.getAttribute("data-prefix")) || "",
                  suffix: (s.suffix ?? el.getAttribute("data-suffix")) || "",
                });
              });
            }
          }

          // nested repeaters (optional): allow child repeaters using "@.<path>"
          if (conf.nestedRepeats) {
            for (const [nestedCls, nConf] of Object.entries(
              conf.nestedRepeats
            )) {
              frag.querySelectorAll(`.${nestedCls}`).forEach((nc) => {
                const nTpl = nConf.template
                  ? nc.querySelector(nConf.template)
                  : nc.querySelector("template");
                if (!nTpl) return;
                [...nc.children].forEach((ch) => {
                  if (ch !== nTpl) ch.remove();
                });

                const arr = getByPath(item, nConf.path.replace(/^@\./, ""));
                if (!Array.isArray(arr)) return;

                arr.forEach((subItem) => {
                  const subFrag = nTpl.content.cloneNode(true);
                  if (nConf.itemMap) {
                    for (const [cls, spec] of Object.entries(nConf.itemMap)) {
                      const nodes = subFrag.querySelectorAll(`.${cls}`);
                      const s =
                        typeof spec === "string" ? { path: spec } : spec;
                      nodes.forEach((el) => {
                        const rawPath = s.path || "";
                        const rel = rawPath.startsWith("@.")
                          ? rawPath.slice(2)
                          : rawPath;
                        const val = getByPath(subItem, rel);
                        setValue(el, val, {
                          attr: s.attr,
                          decimals: s.decimals ?? readNum(el, "data-decimals"),
                          // FIX: wrap ?? part
                          prefix:
                            (s.prefix ?? el.getAttribute("data-prefix")) || "",
                          suffix:
                            (s.suffix ?? el.getAttribute("data-suffix")) || "",
                        });
                      });
                    }
                  }
                  nc.appendChild(subFrag);
                });
              });
            }
          }

          container.appendChild(frag);
        });
      }
    }
  }

  function bindAll(root, data, classMap, repeats) {
    processRepeaters(root, data, repeats);
    bindWithMap(root, data, classMap);
  }

  // -----------------------------
  // Derive scalable view from JSON
  // -----------------------------
  function deriveView(data, taxonomyAreas = []) {
    const plan = data.plans?.[0] ?? {};
    const floor = plan.floors?.[0] ?? {};
    const rooms = Array.isArray(floor.rooms) ? floor.rooms : [];

    const aliasToKey = new Map();
    const keyMeta = new Map();
    (taxonomyAreas || []).forEach((a) => {
      keyMeta.set(a.key, a);
      (a.aliases || []).forEach((al) =>
        aliasToKey.set(al.toLowerCase(), a.key)
      );
      aliasToKey.set(a.key.toLowerCase(), a.key);
    });

    function canonicalFromRoom(r) {
      const labels = [
        r.classificationKey,
        r.type,
        r.name,
        r["classificationKey?"],
        r["name?"],
        r["type?"],
      ]
        .filter(Boolean)
        .map((s) => String(s).toLowerCase());

      let typeKey = null;
      for (const label of labels) {
        if (aliasToKey.has(label)) {
          typeKey = aliasToKey.get(label);
          break;
        }
      }
      if (!typeKey && r.type) typeKey = r.type;

      const meta = typeKey && keyMeta.get(typeKey);
      return {
        typeKey: typeKey || r.type || r.name || "Unknown",
        abbr: meta?.abbr || r.abbr || r["abbr?"] || "",
        group: meta?.group || r.group || r["group?"] || "",
        habitable:
          typeof r.habitable === "boolean"
            ? r.habitable
            : meta?.habitable ?? null,
        wet: typeof r.wetZone === "boolean" ? r.wetZone : meta?.wet ?? null,
      };
    }

    const roomsDecorated = rooms.map((r) => {
      const canon = canonicalFromRoom(r);
      const dims = r.dims_mm || r["dims_mm?"] || null;
      const w = Array.isArray(dims) ? dims[0] : null;
      const h = Array.isArray(dims) ? dims[1] : null;
      return {
        ...r,
        _canon: canon,
        _dims: { w, h },
        _dimText: w && h ? `${w} × ${h} mm` : "",
      };
    });

    const countsByType = new Map();
    for (const r of roomsDecorated) {
      const key = r._canon.typeKey;
      countsByType.set(key, (countsByType.get(key) || 0) + 1);
    }
    const countsByTypeArray = [...countsByType.entries()]
      .map(([typeKey, count]) => {
        const meta = keyMeta.get(typeKey);
        return {
          typeKey,
          abbr: meta?.abbr || "",
          group: meta?.group || "",
          count,
        };
      })
      .sort((a, b) => a.typeKey.localeCompare(b.typeKey));

    const byType = new Map();
    for (const r of roomsDecorated) {
      const key = r._canon.typeKey;
      if (!byType.has(key)) {
        const meta = keyMeta.get(key);
        byType.set(key, {
          typeKey: key,
          abbr: meta?.abbr || "",
          group: meta?.group || "",
          rooms: [],
        });
      }
      byType.get(key).rooms.push(r);
    }
    const roomsByType = [...byType.values()].sort((a, b) =>
      a.typeKey.localeCompare(b.typeKey)
    );

    const doorsObjFloor = floor.counts?.doorsByType || {};
    const doorsByTypeArrayFloor = Object.keys(doorsObjFloor).map((k) => ({
      type: k,
      count: doorsObjFloor[k],
    }));

    const doorsObjTotal = plan.totals?.doorsByType || {};
    const doorsByTypeArrayTotal = Object.keys(doorsObjTotal).map((k) => ({
      type: k,
      count: doorsObjTotal[k],
    }));

    const areasTypeObj = plan.totals?.areasByType_m2 || {};
    const areasByTypeArray = Object.keys(areasTypeObj).map((k) => ({
      typeKey: k,
      area: areasTypeObj[k],
    }));

    const areasGroupObj = plan.totals?.areasByGroup_m2 || {};
    const areasByGroupArray = Object.keys(areasGroupObj).map((k) => ({
      groupKey: k,
      area: areasGroupObj[k],
    }));

    return {
      ...data,
      _view: {
        plan,
        floor,
        rooms: roomsDecorated,
        roomsByType,
        countsByTypeArray,
        doorsByTypeArrayFloor,
        doorsByTypeArrayTotal,
        areasByTypeArray,
        areasByGroupArray,
      },
    };
  }

  // -----------------------------
  // Defaults: classMap & repeats
  // (Rename the class names on the left)
  // -----------------------------
  const DEFAULT_CLASS_MAP = {
    // Meta
    builderName: "plans[0].projectInfo.builder?",
    projectName: "plans[0].projectInfo.projectName?",
    drawnDate: "plans[0].planMeta.drawnDate",
    units: "plans[0].planMeta.units",
    titleBlock: "plans[0].planMeta.titleBlock",
    fileType: "plans[0].planMeta.fileType",
    vectorOrRaster: "plans[0].planMeta.vectorOrRaster",
    rasterMode: "plans[0].planMeta.rasterMode",
    scale: "plans[0].planMeta.scale",

    // Floor summary
    floorLevel: "plans[0].floors[0].level",
    floorPhase: "plans[0].floors[0].phase",

    // Totals
    gfaTotal: "plans[0].totals.GFA_m2?",
    totalWindows: "plans[0].totals.windows",
  };

  const DEFAULT_REPEATS = {
    // All rooms (type-agnostic)
    roomsAll: {
      path: "_view.rooms",
      itemMap: {
        roomID: "@.id",
        roomTypeKey: "@._canon.typeKey",
        roomTypeAbbr: "@._canon.abbr",
        roomGroup: "@._canon.group",
        roomHabitable: "@._canon.habitable",
        roomWet: "@._canon.wet",
        roomName: "@.name?",
        roomDimW: "@._dims.w",
        roomDimH: "@._dims.h",
        roomDimText: "@._dimText",
      },
    },

    // Rooms grouped by type (with nested per-type rooms)
    roomTypes: {
      path: "_view.roomsByType",
      itemMap: {
        typeKey: "@.typeKey",
        typeAbbr: "@.abbr",
        typeGroup: "@.group",
        typeCount: "@.rooms.length",
      },
      nestedRepeats: {
        roomsOfThisType: {
          path: "@.rooms",
          itemMap: {
            subRoomName: "@.name?",
            subRoomId: "@.id",
            subRoomDim: "@._dimText",
          },
        },
      },
    },

    // Dynamic counts by type
    roomCountsDynamic: {
      path: "_view.countsByTypeArray",
      itemMap: {
        countTypeKey: "@.typeKey",
        countTypeAbbr: "@.abbr",
        countTypeGroup: "@.group",
        countValue: "@.count",
      },
    },

    // Doors (floor)
    doorsByTypeFloor: {
      path: "_view.doorsByTypeArrayFloor",
      itemMap: {
        doorType: "@.type",
        doorCount: "@.count",
      },
    },

    // Doors (total)
    doorsByTypeTotal: {
      path: "_view.doorsByTypeArrayTotal",
      itemMap: {
        doorTypeTotal: "@.type",
        doorCountTotal: "@.count",
      },
    },

    // Areas by Type (totals)
    areasByTypeDynamic: {
      path: "_view.areasByTypeArray",
      itemMap: {
        areaTypeKey: "@.typeKey",
        areaValue: "@.area",
      },
    },

    // Areas by Group (totals)
    areasByGroupDynamic: {
      path: "_view.areasByGroupArray",
      itemMap: {
        areaGroupKey: "@.groupKey",
        areaGroupValue: "@.area",
      },
    },
  };

  // -----------------------------
  // Fetch helpers
  // -----------------------------
  async function fetchJSON(url) {
    const r = await fetch(url, { cache: "no-store" });
    if (!r.ok) throw new Error(`Fetch failed: ${url} (${r.status})`);
    return r.json();
  }

  // -----------------------------
  // Public API
  // -----------------------------
  async function init(config) {
    const {
      jsonUrl,
      taxonomyUrl,
      taxonomy,
      root = document,
      classMap = DEFAULT_CLASS_MAP,
      repeats = DEFAULT_REPEATS,
      onData,
    } = config || {};

    if (!jsonUrl) throw new Error('PlanBinder.init: "jsonUrl" is required');

    const [planData, tax] = await Promise.all([
      fetchJSON(jsonUrl),
      taxonomy
        ? Promise.resolve(taxonomy)
        : taxonomyUrl
        ? fetchJSON(taxonomyUrl)
        : Promise.resolve([]),
    ]);

    const areasArray = Array.isArray(tax)
      ? tax
      : tax && Array.isArray(tax.areas)
      ? tax.areas
      : [];
    const dataWithView = deriveView(planData, areasArray);

    if (typeof onData === "function") onData(dataWithView);

    bindAll(root, dataWithView, classMap, repeats);
    return dataWithView;
  }

  window.PlanBinder = {
    init,
    getByPath,
    deriveView,
    bind: (root, data, classMap, repeats) =>
      bindAll(
        root || document,
        data,
        classMap || DEFAULT_CLASS_MAP,
        repeats || DEFAULT_REPEATS
      ),
    defaults: { classMap: DEFAULT_CLASS_MAP, repeats: DEFAULT_REPEATS },
  };
})();

// Build array of report cards from JSON totals + room meta (icon/label)
// - Prefers cross-plan grand totals; falls back to plan[0] totals
// - getRoomMetaFn(typeKey) should return { label, icon }; falls back gracefully
function buildRoomCards(
  data,
  getRoomMetaFn,
  { source = "auto", sortBy = "label", desc = false, hideZero = false } = {}
) {
  // Choose source of counts
  let counts = {};
  if (source === "cross" || source === "auto") {
    counts = data?.crossPlansSummary?.grandTotals?.roomsByType || {};
  }
  if (
    (!counts || !Object.keys(counts).length) &&
    (source === "plan" || source === "auto")
  ) {
    counts = data?.plans?.[0]?.totals?.roomsByType || {};
  }

  // Convert to array with meta
  let cards = Object.entries(counts).map(([type, count]) => {
    const meta =
      typeof getRoomMetaFn === "function" ? getRoomMetaFn(type) : null;
    const label = meta?.label || type || "Unknown";
    const icon = meta?.icon || window.ROOM_META?.ROOM_ICON_DEFAULT || "";
    return { type, label, icon, count: Number(count) || 0 };
  });

  // Optional: hide zero-counts
  if (hideZero) cards = cards.filter((c) => c.count > 0);

  // Sort
  cards.sort((a, b) => {
    let vA = a[sortBy],
      vB = b[sortBy];
    if (typeof vA === "string") vA = vA.toLowerCase();
    if (typeof vB === "string") vB = vB.toLowerCase();
    if (vA < vB) return desc ? 1 : -1;
    if (vA > vB) return desc ? -1 : 1;
    return 0;
  });

  return cards;
}
